/**
 * v2.72 — dataSync — central cache-invalidation / live-refresh bus.
 *
 * WHY
 * ───
 * The app intentionally has no React-Query/SWR; every page owns its fetch.
 * That kept pages simple but meant a mutation in one component (delete a
 * property, approve a listing, update a lead) left every OTHER mounted
 * list/dashboard stale until a manual refresh. This module is the missing
 * glue: a tiny pub/sub keyed by data DOMAIN.
 *
 * CANONICAL DOMAINS
 * ─────────────────
 *   "properties"   — listings (create/edit/delete/approve/sold/feature)
 *   "approvals"    — admin pending-approval queue
 *   "projects"     — builder projects
 *   "leads"        — behavioural leads / CRM pipeline
 *   "enquiries"    — form enquiries
 *   "users"        — user accounts (create/block/delete)
 *   "dealers"      — dealer roster / KYC / plans
 *   "dashboard"    — aggregate counters (admin overview etc.)
 *
 * USAGE
 * ─────
 *   // after ANY successful mutation:
 *   emitDataChange(["properties", "dashboard"], { action: "delete", id });
 *
 *   // in any component that renders that data:
 *   useDataSync(["properties"], (domains, payload) => refetch());
 *
 *   // background polling (visibility-aware — no requests in hidden tabs):
 *   useAutoRefresh(() => refetch(true), 45000);
 *
 * EXTRAS
 * ──────
 *   • "properties" events auto-invalidate the LRU search cache
 *     (features/property/cache.js) so public search never serves stale rows.
 *   • Events replicate across same-browser tabs via BroadcastChannel —
 *     an admin editing in tab A updates the dashboard open in tab B.
 *   • The optional payload `{ action: "delete"|"patch", id, patch }` lets
 *     subscribers apply an optimistic local update instantly, then reconcile
 *     with the silent refetch.
 */

import { useEffect, useRef } from "react";
import { propertyCache } from "../features/property/cache";

const listeners = new Set(); // { domains: Set<string>|null, fn }

function notifyLocal(domains, payload) {
  if (domains.includes("properties")) {
    // Public search LRU (60 s TTL) must never serve pre-mutation results.
    propertyCache.invalidatePrefix("search");
  }
  for (const l of Array.from(listeners)) {
    if (!l.domains || domains.some((d) => l.domains.has(d))) {
      try { l.fn(domains, payload); } catch (_) { /* never break the bus */ }
    }
  }
}

/* Cross-tab replication — best-effort (Safari <15.4 lacks BroadcastChannel). */
let channel = null;
try {
  channel = new BroadcastChannel("he-data-sync");
  channel.onmessage = (e) => {
    const { domains, payload } = e.data || {};
    if (Array.isArray(domains) && domains.length) notifyLocal(domains, payload || null);
  };
} catch (_) { channel = null; }

/**
 * Announce that data in the given domain(s) changed. Call AFTER the mutation
 * API resolves successfully.
 * @param {string|string[]} domains  one or more canonical domain names
 * @param {{action?:"delete"|"patch", id?:string, patch?:Object}|null} payload
 *        optional optimistic-update hint for subscribers
 */
export function emitDataChange(domains, payload = null) {
  const arr = Array.isArray(domains) ? domains : [domains];
  notifyLocal(arr, payload);
  try { channel?.postMessage({ domains: arr, payload }); } catch (_) { /* noop */ }
}

/**
 * Subscribe a refetch callback to one or more domains. The callback ref is
 * kept fresh — no stale closures, no need to memoise the callback.
 */
export function useDataSync(domains, fn) {
  const fnRef = useRef(fn);
  fnRef.current = fn;
  const key = (domains || []).join(",");
  useEffect(() => {
    const l = {
      domains: key ? new Set(key.split(",")) : null,
      fn: (d, p) => fnRef.current?.(d, p),
    };
    listeners.add(l);
    return () => { listeners.delete(l); };
  }, [key]);
}

/**
 * v2.79 Phase 1 — Low-level (non-hook) subscribe. Use from Zustand stores
 * or any other React-less module that needs to react to mutations.
 * Returns an unsubscribe function.
 *
 * @param {string|string[]} domains  one or more canonical domain names
 * @param {(domains:string[], payload:any) => void} fn
 * @returns {() => void}  unsubscribe
 */
export function subscribeDataChange(domains, fn) {
  const arr = Array.isArray(domains) ? domains : [domains];
  const l = { domains: arr.length ? new Set(arr) : null, fn };
  listeners.add(l);
  return () => { listeners.delete(l); };
}

/**
 * Visibility-aware background polling. Fires `fn` every `intervalMs` ONLY
 * while the tab is visible; additionally fires once when the user returns
 * to a tab that has been hidden for a while (15 s+), so "switch back to the
 * dashboard" always shows fresh numbers.
 */
export function useAutoRefresh(fn, intervalMs = 45000, enabled = true) {
  const fnRef = useRef(fn);
  fnRef.current = fn;
  useEffect(() => {
    if (!enabled || !intervalMs) return undefined;
    let last = Date.now();
    const fire = () => { last = Date.now(); fnRef.current?.(); };
    const id = setInterval(() => {
      if (document.visibilityState === "visible") fire();
    }, intervalMs);
    const onReturn = () => {
      if (document.visibilityState === "visible" && Date.now() - last > Math.min(intervalMs, 15000)) fire();
    };
    document.addEventListener("visibilitychange", onReturn);
    window.addEventListener("focus", onReturn);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onReturn);
      window.removeEventListener("focus", onReturn);
    };
  }, [intervalMs, enabled]);
}
